/**
 * Device modelling (migration 20261002170000): device types defined once with
 * their component templates, and devices built from them.
 *
 * Pure — no I/O — so the vocabulary here can be held to the database's CHECK
 * constraints by tests/dcim.test.ts, and the template form can only send
 * what the table accepts.
 */

// ─── Vocabulary (mirrors the CHECK lists in the migration) ──────────────────

export const COMPONENT_KINDS = [
  'interface',
  'console_port',
  'console_server_port',
  'power_port',
  'power_outlet',
  'front_port',
  'rear_port',
  'module_bay',
  'device_bay',
] as const;
export type ComponentKind = (typeof COMPONENT_KINDS)[number];

export const COMPONENT_KIND_LABEL: Record<ComponentKind, string> = {
  interface: 'Interface',
  console_port: 'Console port',
  console_server_port: 'Console server port',
  power_port: 'Power port',
  power_outlet: 'Power outlet',
  front_port: 'Front port',
  rear_port: 'Rear port',
  module_bay: 'Module bay',
  device_bay: 'Device bay',
};

export const DEVICE_STATUSES = ['planned', 'staged', 'active', 'offline', 'failed', 'decommissioning', 'inventory'] as const;
export type DeviceStatus = (typeof DEVICE_STATUSES)[number];

export const DEVICE_CONFIDENCES = ['verified', 'deduced', 'contested', 'unknown'] as const;
export type DeviceConfidence = (typeof DEVICE_CONFIDENCES)[number];

export const DEVICE_TYPE_KINDS = [
  'switch', 'router', 'firewall', 'gateway', 'patch_panel', 'fiber', 'server', 'ups', 'pdu',
  'shelf', 'phone_system', 'kvm', 'access_point', 'camera', 'other',
] as const;
export type DeviceTypeKind = (typeof DEVICE_TYPE_KINDS)[number];

export const AIRFLOWS = ['front-to-rear', 'rear-to-front', 'left-to-right', 'right-to-left', 'side-to-rear', 'passive', 'mixed'] as const;

export const ROLE_COLOR_TOKENS = ['info', 'accent-purple', 'accent-gold', 'accent-orange', 'success', 'warning', 'error', 'notification'] as const;

/** Suggested `type` values per kind. Free text in the database; these keep spelling consistent. */
export const COMPONENT_TYPE_SUGGESTIONS: Partial<Record<ComponentKind, string[]>> = {
  interface: [
    '1000base-t', '2.5gbase-t', '10gbase-t', '1000base-x-sfp', '10gbase-x-sfpp', '25gbase-x-sfp28',
    '40gbase-x-qsfpp', '100gbase-x-qsfp28', 'ieee802.11ac', 'ieee802.11ax', 'ieee802.11be', 'lag', 'virtual',
  ],
  console_port: ['rj-45', 'usb-c', 'usb-micro-b', 'de-9'],
  console_server_port: ['rj-45', 'usb-a', 'de-9'],
  power_port: ['iec-60320-c14', 'iec-60320-c20', 'nema-5-15p', 'nema-l5-30p', 'dc-terminal', 'poe'],
  power_outlet: ['iec-60320-c13', 'iec-60320-c19', 'nema-5-15r', 'nema-5-20r'],
  front_port: ['8p8c', 'lc', 'lc-apc', 'sc', 'sc-apc', 'mpo', 'st'],
  rear_port: ['8p8c', '110-punch', 'lc', 'sc', 'mpo', 'splice'],
};

// ─── Name patterns ──────────────────────────────────────────────────────────

/**
 * Expands bracketed ranges the way engineers write port names:
 *   "Gi1/0/[1-48]"    → Gi1/0/1 … Gi1/0/48
 *   "Port [01-24]"    → Port 01 … Port 24 (zero padding kept)
 *   "PSU[A,B]"        → PSUA, PSUB
 *   "[a-c][1-2]"      → a1, a2, b1, b2, c1, c2
 * Throws on a malformed or oversized pattern so the form can say why.
 */
export function expandNamePattern(pattern: string, limit = 1024): string[] {
  const trimmed = pattern.trim();
  if (!trimmed) return [];
  const parts: string[][] = [];
  let i = 0;
  let literal = '';
  while (i < trimmed.length) {
    const ch = trimmed[i];
    if (ch === ']') throw new Error(`Unmatched "]" in "${pattern}".`);
    if (ch !== '[') {
      literal += ch;
      i++;
      continue;
    }
    const close = trimmed.indexOf(']', i);
    if (close === -1) throw new Error(`Unclosed "[" in "${pattern}".`);
    if (literal) parts.push([literal]);
    literal = '';
    parts.push(expandGroup(trimmed.slice(i + 1, close), pattern));
    i = close + 1;
  }
  if (literal) parts.push([literal]);

  const total = parts.reduce((n, p) => n * p.length, 1);
  if (total > limit) throw new Error(`"${pattern}" expands to ${total} names; the limit is ${limit}.`);
  return parts.reduce<string[]>((acc, options) => acc.flatMap((prefix) => options.map((o) => prefix + o)), ['']);
}

function expandGroup(body: string, pattern: string): string[] {
  const out: string[] = [];
  for (const raw of body.split(',')) {
    const item = raw.trim();
    if (!item) throw new Error(`Empty item in "[${body}]" of "${pattern}".`);
    const numeric = /^(\d+)-(\d+)$/.exec(item);
    const alpha = /^([a-zA-Z])-([a-zA-Z])$/.exec(item);
    if (numeric) {
      const [, a, b] = numeric;
      const from = Number(a);
      const to = Number(b);
      if (to < from) throw new Error(`Range "${item}" runs backwards in "${pattern}".`);
      const width = a.length > 1 && a.startsWith('0') ? a.length : 0;
      for (let n = from; n <= to; n++) out.push(width ? String(n).padStart(width, '0') : String(n));
    } else if (alpha) {
      const from = alpha[1].charCodeAt(0);
      const to = alpha[2].charCodeAt(0);
      if (to < from) throw new Error(`Range "${item}" runs backwards in "${pattern}".`);
      for (let c = from; c <= to; c++) out.push(String.fromCharCode(c));
    } else {
      out.push(item);
    }
  }
  return out;
}

// ─── Template drafts → insertable rows ──────────────────────────────────────

export interface TemplateDraft {
  kind: ComponentKind;
  namePattern: string;
  labelPattern?: string;
  type?: string;
  description?: string;
  mgmtOnly?: boolean;
  poeMode?: 'pd' | 'pse' | '';
  maximumDrawW?: number | null;
  allocatedDrawW?: number | null;
  feedLeg?: 'A' | 'B' | 'C' | '';
  powerPortTemplateId?: string | null;
  /** Rear ports: positions per port (default 1). */
  positions?: number | null;
  /** Front ports: the rear port they pass through, and the first position used. */
  rearPortTemplateId?: string | null;
  rearPortStartPosition?: number | null;
  /** Module bays: the {module} substitution. Expanded alongside the name when a pattern. */
  positionPattern?: string;
}

export interface TemplateInsert {
  device_type_id: string | null;
  module_type_id: string | null;
  kind: ComponentKind;
  name: string;
  label: string | null;
  type: string | null;
  description: string | null;
  mgmt_only: boolean;
  poe_mode: 'pd' | 'pse' | null;
  maximum_draw_w: number | null;
  allocated_draw_w: number | null;
  feed_leg: 'A' | 'B' | 'C' | null;
  power_port_template_id: string | null;
  positions: number | null;
  rear_port_template_id: string | null;
  rear_port_position: number | null;
  position: string | null;
  sort_order: number;
}

export type TemplateOwner = { deviceTypeId: string } | { moduleTypeId: string };

const blank = (v: string | undefined | null) => {
  const t = (v ?? '').trim();
  return t === '' ? null : t;
};

/**
 * Turns one form submission into template rows. Every field a kind may not
 * carry is forced to NULL/false, matching the table's CHECK constraints, so a
 * value left in a hidden input can never reach the database.
 */
export function buildTemplateRows(
  owner: TemplateOwner,
  draft: TemplateDraft,
  opts: { startSortOrder?: number; rearPortPositions?: number | null } = {},
): { rows: TemplateInsert[]; error: string | null } {
  if (draft.kind === 'device_bay' && 'moduleTypeId' in owner) return { rows: [], error: 'Module types cannot have device bays.' };
  let names: string[];
  let labels: (string | null)[];
  let positions: (string | null)[];
  try {
    names = expandNamePattern(draft.namePattern);
    const labelList = draft.labelPattern?.trim() ? expandNamePattern(draft.labelPattern) : [];
    const positionList = draft.kind === 'module_bay' && draft.positionPattern?.trim() ? expandNamePattern(draft.positionPattern) : [];
    if (labelList.length && labelList.length !== names.length) {
      return { rows: [], error: `The label pattern gives ${labelList.length} labels for ${names.length} names.` };
    }
    if (positionList.length && positionList.length !== names.length) {
      return { rows: [], error: `The position pattern gives ${positionList.length} positions for ${names.length} names.` };
    }
    labels = names.map((_, i) => labelList[i] ?? null);
    positions = names.map((_, i) => positionList[i] ?? null);
  } catch (e) {
    return { rows: [], error: e instanceof Error ? e.message : String(e) };
  }
  if (names.length === 0) return { rows: [], error: 'A name is required.' };
  if (new Set(names).size !== names.length) return { rows: [], error: 'The pattern produces duplicate names.' };

  const k = draft.kind;
  const maxDraw = k === 'power_port' ? draft.maximumDrawW ?? null : null;
  const allocDraw = k === 'power_port' ? draft.allocatedDrawW ?? null : null;
  if (maxDraw !== null && (!Number.isInteger(maxDraw) || maxDraw <= 0)) return { rows: [], error: 'Maximum draw must be a whole number of watts.' };
  if (allocDraw !== null && (!Number.isInteger(allocDraw) || allocDraw <= 0)) return { rows: [], error: 'Allocated draw must be a whole number of watts.' };
  if (maxDraw !== null && allocDraw !== null && allocDraw > maxDraw) return { rows: [], error: 'Allocated draw cannot exceed maximum draw.' };

  let rearPositions: number | null = null;
  if (k === 'rear_port') {
    rearPositions = draft.positions ?? 1;
    if (!Number.isInteger(rearPositions) || rearPositions < 1 || rearPositions > 1024) {
      return { rows: [], error: 'Positions must be a whole number from 1 to 1024.' };
    }
  }

  let frontStart: number | null = null;
  if (k === 'front_port') {
    if (!draft.rearPortTemplateId) return { rows: [], error: 'Choose the rear port these front ports pass through.' };
    frontStart = draft.rearPortStartPosition ?? 1;
    if (!Number.isInteger(frontStart) || frontStart < 1) return { rows: [], error: 'Start position must be 1 or more.' };
    const last = frontStart + names.length - 1;
    if (opts.rearPortPositions != null && last > opts.rearPortPositions) {
      return { rows: [], error: `The rear port has ${opts.rearPortPositions} position(s); ${names.length} front port(s) from ${frontStart} would need ${last}.` };
    }
  }

  const start = opts.startSortOrder ?? 0;
  const rows = names.map<TemplateInsert>((name, i) => ({
    device_type_id: 'deviceTypeId' in owner ? owner.deviceTypeId : null,
    module_type_id: 'moduleTypeId' in owner ? owner.moduleTypeId : null,
    kind: k,
    name,
    label: labels[i],
    type: blank(draft.type),
    description: blank(draft.description),
    mgmt_only: k === 'interface' ? Boolean(draft.mgmtOnly) : false,
    poe_mode: k === 'interface' && draft.poeMode ? draft.poeMode : null,
    maximum_draw_w: maxDraw,
    allocated_draw_w: allocDraw,
    feed_leg: k === 'power_outlet' && draft.feedLeg ? draft.feedLeg : null,
    power_port_template_id: k === 'power_outlet' ? draft.powerPortTemplateId ?? null : null,
    positions: rearPositions,
    rear_port_template_id: k === 'front_port' ? draft.rearPortTemplateId ?? null : null,
    rear_port_position: frontStart === null ? null : frontStart + i,
    position: k === 'module_bay' ? positions[i] : null,
    sort_order: start + i,
  }));
  return { rows, error: null };
}

// ─── Reading a type ─────────────────────────────────────────────────────────

export interface TemplateRow {
  id: string;
  device_type_id: string | null;
  module_type_id: string | null;
  kind: ComponentKind;
  name: string;
  label: string | null;
  type: string | null;
  description: string | null;
  mgmt_only: boolean;
  poe_mode: string | null;
  maximum_draw_w: number | null;
  allocated_draw_w: number | null;
  feed_leg: string | null;
  power_port_template_id: string | null;
  positions: number | null;
  rear_port_template_id: string | null;
  rear_port_position: number | null;
  position: string | null;
  sort_order: number;
}

export type KindCounts = Partial<Record<ComponentKind, number>>;

export function countByKind<T extends { kind: ComponentKind }>(rows: T[]): KindCounts {
  const out: KindCounts = {};
  for (const r of rows) out[r.kind] = (out[r.kind] ?? 0) + 1;
  return out;
}

/** "48 interfaces · 1 console port · 2 power ports" in COMPONENT_KINDS order. */
export function summarizeCounts(counts: KindCounts): string {
  const parts: string[] = [];
  for (const kind of COMPONENT_KINDS) {
    const n = counts[kind];
    if (!n) continue;
    const label = COMPONENT_KIND_LABEL[kind].toLowerCase();
    parts.push(`${n} ${n === 1 ? label : label + 's'}`);
  }
  return parts.length ? parts.join(' · ') : 'No components';
}

/**
 * Natural order for port names: "Gi1/0/2" before "Gi1/0/10", grouped by kind
 * in COMPONENT_KINDS order, then by the template's sort_order.
 */
export function compareComponents(
  a: { kind: ComponentKind; name: string; sort_order: number },
  b: { kind: ComponentKind; name: string; sort_order: number },
): number {
  return (
    COMPONENT_KINDS.indexOf(a.kind) - COMPONENT_KINDS.indexOf(b.kind) ||
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }) ||
    a.sort_order - b.sort_order
  );
}

/** Rear-port template id → highest position already taken by a front port. */
export function usedRearPositions(templates: TemplateRow[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const t of templates) {
    if (t.kind !== 'front_port' || !t.rear_port_template_id || t.rear_port_position == null) continue;
    out.set(t.rear_port_template_id, Math.max(out.get(t.rear_port_template_id) ?? 0, t.rear_port_position));
  }
  return out;
}

/** "Ubiquiti USW-Pro-Max-48-PoE". */
export function deviceTypeLabel(manufacturer: string | null | undefined, model: string): string {
  return manufacturer ? `${manufacturer} ${model}` : model;
}

/**
 * The rack position a device links to says how many RU it occupies; the type
 * says how many it should. A mismatch is surfaced, never corrected silently —
 * either the survey or the type is wrong and only a person can tell which.
 */
export function rackHeightMismatch(typeUHeight: number, unitRuHeight: number | null | undefined): string | null {
  if (unitRuHeight == null || typeUHeight === 0) return null;
  if (Math.ceil(typeUHeight) === unitRuHeight) return null;
  return `Type is ${typeUHeight}U; the surveyed position spans ${unitRuHeight}U.`;
}

/** Normalises a MAC to lower-case colon form, or null when it is not one. */
export function normalizeMac(input: string): string | null {
  const hex = input.trim().toLowerCase().replace(/[^0-9a-f]/g, '');
  if (hex.length !== 12) return null;
  if (!/^[0-9a-f:.\-\s]+$/i.test(input.trim())) return null;
  return hex.match(/.{2}/g)!.join(':');
}
